/**
 * Utility functions for tracking chatbot analytics
 */

// Store analytics data in localStorage for demo purposes
// In a production environment, this would send data to a backend service
const ANALYTICS_KEY = 'laprecio_chatbot_analytics';

/**
 * Initialize analytics storage
 */
export const initAnalytics = () => {
  if (!localStorage.getItem(ANALYTICS_KEY)) {
    localStorage.setItem(ANALYTICS_KEY, JSON.stringify({
      conversations: [],
      totalInteractions: 0,
      topQueries: {},
      sessionCount: 0,
      feedback: [],
      improvementSuggestions: []
    }));
  }
};

/**
 * Track a new conversation session
 * @returns {string} - Session ID
 */
export const trackNewSession = () => {
  const sessionId = `session_${Date.now()}`;
  const analytics = JSON.parse(localStorage.getItem(ANALYTICS_KEY));

  analytics.sessionCount += 1;
  analytics.conversations.push({
    sessionId,
    startTime: new Date().toISOString(),
    messages: [],
    resolved: false
  });

  localStorage.setItem(ANALYTICS_KEY, JSON.stringify(analytics));
  return sessionId;
};

/**
 * Track a user message
 * @param {string} sessionId - The current session ID
 * @param {string} message - The user's message
 */
export const trackUserMessage = (sessionId, message) => {
  const analytics = JSON.parse(localStorage.getItem(ANALYTICS_KEY));
  const sessionIndex = analytics.conversations.findIndex(c => c.sessionId === sessionId);

  if (sessionIndex !== -1) {
    // Add message to conversation
    analytics.conversations[sessionIndex].messages.push({
      type: 'user',
      text: message,
      timestamp: new Date().toISOString()
    });

    // Update total interactions
    analytics.totalInteractions += 1;

    // Track query frequency
    const normalizedQuery = message.toLowerCase().trim();
    analytics.topQueries[normalizedQuery] = (analytics.topQueries[normalizedQuery] || 0) + 1;

    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(analytics));
  }
};

/**
 * Track a bot response
 * @param {string} sessionId - The current session ID
 * @param {string} message - The bot's response
 * @param {number} confidence - Confidence score of the response
 */
export const trackBotResponse = (sessionId, message, confidence = 1) => {
  const analytics = JSON.parse(localStorage.getItem(ANALYTICS_KEY));
  const sessionIndex = analytics.conversations.findIndex(c => c.sessionId === sessionId);

  if (sessionIndex !== -1) {
    // Add message to conversation
    analytics.conversations[sessionIndex].messages.push({
      type: 'bot',
      text: message,
      confidence,
      timestamp: new Date().toISOString()
    });

    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(analytics));
  }
};

/**
 * Mark a conversation as resolved
 * @param {string} sessionId - The session ID to mark as resolved
 */
export const markConversationResolved = (sessionId) => {
  const analytics = JSON.parse(localStorage.getItem(ANALYTICS_KEY));
  const sessionIndex = analytics.conversations.findIndex(c => c.sessionId === sessionId);

  if (sessionIndex !== -1) {
    analytics.conversations[sessionIndex].resolved = true;
    analytics.conversations[sessionIndex].endTime = new Date().toISOString();

    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(analytics));
  }
};

/**
 * Track user feedback on a bot message
 * @param {string} messageId - The ID of the message receiving feedback
 * @param {boolean} isPositive - Whether the feedback is positive
 * @param {string} feedbackText - Additional feedback text for negative feedback
 */
export const trackFeedback = (messageId, isPositive, feedbackText = '') => {
  const analytics = JSON.parse(localStorage.getItem(ANALYTICS_KEY));

  // Add to feedback array
  analytics.feedback.push({
    messageId,
    isPositive,
    timestamp: new Date().toISOString()
  });

  // If negative feedback with text, add to improvement suggestions
  if (!isPositive && feedbackText.trim()) {
    analytics.improvementSuggestions.push({
      messageId,
      suggestion: feedbackText,
      timestamp: new Date().toISOString()
    });
  }

  localStorage.setItem(ANALYTICS_KEY, JSON.stringify(analytics));
};

/**
 * Get analytics data
 * @returns {object} - The analytics data
 */
export const getAnalytics = () => {
  return JSON.parse(localStorage.getItem(ANALYTICS_KEY));
};

// Initialize analytics on module load
initAnalytics();
